import moment from 'moment-timezone';
import React, { useCallback, useState } from 'react';

import type { PendingTeamMembership } from '@/auth/hooks/useIsSMBIncomplete';
import Alert from '@/components/Alert';
import Button from '@/components/Button';
import FAIcon from '@/components/Icon/FAIcon';
import Link from '@/components/Link';
import type { Viewer } from '@/core/viewer';
import RedirectCompleteness from '@/profile/components/ProfileCompleteness/RedirectCompleteness';
import { darkGreen } from '@/theme/colors';

import { PendingTeamApprovalBanner } from './PendingTeamApprovalBanner';
import { SetupPausedBannerSurface } from './SetupPausedBanner/SetupPausedBannerSurface';

interface Props {
  viewer: Viewer;
  pendingTeamMembership: PendingTeamMembership | null;
}

// Rendered in place of the regular dashboard when the viewer is an SMB user
// who hasn't completed team billing setup (see useIsSMBIncomplete). Mirrors
// the chrome the legacy Dashboard.tsx kept in its banner branches: shared
// alerts (terms/privacy, expert agreement, password expiry) and
// RedirectCompleteness. Drops ConsultationsStarting + AppNotifications since
// SMB-incomplete users have nothing scheduled or to notify on.
export function SMBResumeSurface({ viewer, pendingTeamMembership }: Props): React.ReactNode {
  const [passwordUpdateClosed, setPasswordUpdate] = useState(false);
  const [, setProfileCompleted] = useState(false);
  const handlePasswordUpdateClose = useCallback(() => setPasswordUpdate(true), []);
  const handleProfileCompleted = useCallback((b: boolean) => setProfileCompleted(b), []);

  // ENG-5570: agreement banners removed. A gated expert never reaches this
  // surface; they are redirected to /legal/accept first.
  const alerts: React.ReactNode[] = [];

  if (!passwordUpdateClosed && viewer.password_expiry && viewer.password_expiry.expiring) {
    alerts.push(
      <Alert
        icon={<FAIcon color={darkGreen} icon="lock" />}
        preset="notification"
        onRequestClose={handlePasswordUpdateClose}
        key="password-update-alert"
      >
        <div>
          To increase security, we ask users to update their password periodically. Your password
          will expire at {moment(viewer.password_expiry.expiry).format('MMM DD')}.
          <br />
          <br />
          <Link to="/settings/password">
            <Button variant="contained" size="medium">
              Update Password
            </Button>
          </Link>
        </div>
      </Alert>
    );
  }

  return (
    <div id="dashboard-root">
      {alerts}
      <RedirectCompleteness profile={viewer.profile} onProfileCompleted={handleProfileCompleted} />
      {pendingTeamMembership ? (
        <PendingTeamApprovalBanner
          firstName={viewer.first_name}
          email={viewer.email?.address}
          teamName={pendingTeamMembership.teamName ?? ''}
        />
      ) : (
        <SetupPausedBannerSurface firstName={viewer.first_name} email={viewer.email?.address} />
      )}
    </div>
  );
}
